/**
 * Taux de réussite des cartes acquises par semaine face à l'objectif de mémorisation
 * (design system, GraphiqueRetention) : courbe interrompue sur une semaine sans révision de
 * carte acquise, objectif en pointillé, axe de 100 % au premier multiple de 5 utile.
 */
import { useRef } from 'react';
import type { RetentionDeLaSemaine } from '../../seance/statistiques';
import {
  BAS,
  CadreDeGraphique,
  HAUT,
  HAUTEUR,
  largeurTexte,
  nombre,
  pourcent,
  useColonnes,
  useInfobulle,
  type LigneDuTableau,
} from './Graphique';

/** En dessous, le taux d'une semaine est trop incertain : son point est creux. */
const REVISIONS_SIGNIFICATIVES = 5;

export const tauxDeLaSemaine = (s: RetentionDeLaSemaine) =>
  s.revisions ? (100 * (s.revisions - s.aRevoir)) / s.revisions : null;

export interface GraphiqueRetentionProps {
  resume: string;
  semaines: RetentionDeLaSemaine[];
  /** Objectif de mémorisation, en pourcentage. */
  objectif: number;
  libelleObjectif: string;
  nomSemaine: (i: number) => string;
  etiquette: (i: number) => string | null;
  nomDuGroupe: string;
  vide: { titre: string; texte: string } | null;
  chargement: boolean;
  tableau: { legende: string; entetes: string[]; lignes: LigneDuTableau[] };
}

const P = 'fc-graphique-retention';

export function GraphiqueRetention({
  resume,
  semaines,
  objectif,
  libelleObjectif,
  nomSemaine,
  etiquette,
  nomDuGroupe,
  vide,
  chargement,
  tableau,
}: GraphiqueRetentionProps) {
  const n = semaines.length;
  const { actif, courant, groupe, trace, survoler, quitter } = useColonnes(n, n - 1);
  const bulle = useRef<HTMLDivElement>(null);
  const taux = semaines.map(tauxDeLaSemaine);

  const phrase = (s: RetentionDeLaSemaine, i: number) => {
    const t = taux[i];
    return t == null
      ? `${nomSemaine(i)} : aucune révision de carte acquise`
      : `${nomSemaine(i)} : ${pourcent(t)} de réussite, ${nombre(s.revisions - s.aRevoir)} réponses réussies sur ${nombre(s.revisions)}`;
  };

  return (
    <CadreDeGraphique
      classe={P}
      titre="Taux de réussite"
      resume={resume}
      vide={vide}
      chargement={chargement}
      tableau={tableau}
      trace={trace}
      legende={
        <>
          <li className={`${P}__cle`}>
            <span className={`${P}__pastille ${P}__pastille--courbe`} />
            Cartes acquises
          </li>
          <li className={`${P}__cle`}>
            <span className={`${P}__pastille ${P}__pastille--objectif`} />
            {libelleObjectif}
          </li>
        </>
      }
    >
      {(L) => {
        if (!L || n === 0) return null;
        const HT = HAUTEUR;
        const H = HAUT + HT + BAS;
        // De 100 % jusqu'au multiple de 5 sous la plus basse valeur (objectif compris) ; 80 % au plus haut.
        const valeurs = taux.filter((t): t is number => t != null);
        const bas = Math.min(80, Math.floor((Math.min(...valeurs, objectif) - 1) / 5) * 5);
        const pas = 100 - bas > 25 ? 10 : 5;
        const y = (v: number) => HAUT + HT - ((v - bas) / (100 - bas)) * HT;
        const graduations: number[] = [];
        for (let v = bas; v <= 100; v += pas) graduations.push(v);
        const G = Math.ceil(Math.max(...graduations.map((v) => largeurTexte(pourcent(v))))) + 8;
        const pasX = (L - G) / n;
        const cx = (i: number) => Math.round(G + (i + 0.5) * pasX);
        const yo = Math.round(y(objectif)) + 0.5;
        let d = '';
        let ouvert = false;
        taux.forEach((t, i) => {
          if (t == null) {
            ouvert = false;
            return;
          }
          d += `${ouvert ? 'L' : 'M'}${cx(i)} ${y(t).toFixed(1)}`;
          ouvert = true;
        });
        const derniere = taux[n - 1];
        const semaineActive = actif >= 0 ? semaines[actif] : undefined;
        const tauxActif = actif >= 0 ? taux[actif] : null;

        return (
          <>
            <svg
              className={`${P}__svg`}
              width={L}
              height={H}
              viewBox={`0 0 ${L} ${H}`}
              onPointerMove={(e) => survoler(e, G, pasX)}
              onPointerLeave={quitter}
            >
              <g aria-hidden="true">
                {graduations.map((v) => (
                  <text
                    key={`t${v}`}
                    className={`${P}__graduation`}
                    textAnchor="end"
                    dy="0.32em"
                    x={G - 8}
                    y={y(v)}
                  >
                    {pourcent(v)}
                  </text>
                ))}
                {graduations
                  .filter((v) => v !== objectif)
                  .map((v) => {
                    const yy = Math.round(y(v)) + 0.5;
                    return (
                      <line
                        key={`l${v}`}
                        className={`${P}__grille`}
                        x1={G}
                        x2={L}
                        y1={yy}
                        y2={yy}
                      />
                    );
                  })}
              </g>
              <line className={`${P}__objectif`} x1={G} x2={L} y1={yo} y2={yo} aria-hidden="true" />
              <line
                className={`${P}__reticule`}
                x1={0}
                x2={0}
                y1={HAUT - 8}
                y2={HAUT + HT}
                visibility={actif < 0 ? 'hidden' : 'visible'}
                style={actif >= 0 ? { transform: `translateX(${cx(actif) + 0.5}px)` } : undefined}
                aria-hidden="true"
              />
              <path className={`${P}__courbe`} d={d} aria-hidden="true" />
              <g role="group" aria-label={nomDuGroupe} ref={groupe}>
                {semaines.map((s, i) => {
                  const t = taux[i];
                  return (
                    <g
                      key={s.debut}
                      className={`${P}__colonne`}
                      role="img"
                      tabIndex={i === courant ? 0 : -1}
                      aria-label={phrase(s, i)}
                      data-index={i}
                      data-actif={i === actif ? '' : undefined}
                    >
                      <rect
                        className={`${P}__cible`}
                        x={G + i * pasX + 1}
                        y={HAUT - 8}
                        width={Math.max(0, pasX - 2)}
                        height={HT + 8}
                        rx={4}
                      />
                      {t != null && (
                        <circle
                          className={`${P}__point`}
                          // Moins de 5 révisions : le taux de la semaine compte peu, point creux.
                          data-peu={s.revisions < REVISIONS_SIGNIFICATIVES ? '' : undefined}
                          cx={cx(i)}
                          cy={y(t).toFixed(1)}
                          r={4}
                        />
                      )}
                    </g>
                  );
                })}
              </g>
              {derniere != null && (
                <text
                  className={`${P}__valeur`}
                  x={cx(n - 1)}
                  y={Math.round(y(derniere)) - 12}
                  textAnchor="middle"
                  aria-hidden="true"
                >
                  {pourcent(derniere)}
                </text>
              )}
              <g aria-hidden="true">
                {semaines.map((s, i) => {
                  const texte = etiquette(i);
                  return texte === null ? null : (
                    <text
                      key={s.debut}
                      className={`${P}__graduation`}
                      x={cx(i)}
                      y={HAUT + HT + 18}
                      textAnchor="middle"
                    >
                      {texte}
                    </text>
                  );
                })}
              </g>
            </svg>
            <Infobulle
              bulle={bulle}
              actif={actif}
              titre={actif >= 0 ? nomSemaine(actif) : ''}
              semaine={semaineActive}
              taux={tauxActif ?? null}
              objectif={objectif}
              centre={actif >= 0 ? cx(actif) : 0}
              largeur={L}
            />
          </>
        );
      }}
    </CadreDeGraphique>
  );
}

function Infobulle({
  bulle,
  actif,
  titre,
  semaine,
  taux,
  objectif,
  centre,
  largeur,
}: {
  bulle: React.RefObject<HTMLDivElement | null>;
  actif: number;
  titre: string;
  semaine: RetentionDeLaSemaine | undefined;
  taux: number | null;
  objectif: number;
  centre: number;
  largeur: number;
}) {
  useInfobulle(bulle, actif, centre, 12, largeur);
  return (
    <div className={`${P}__infobulle`} aria-hidden="true" hidden={!semaine} ref={bulle}>
      {semaine && (
        <>
          <p className={`${P}__infobulle-titre`}>{titre}</p>
          {taux == null ? (
            <p className={`${P}__aucune`}>Aucune révision de carte acquise</p>
          ) : (
            <div className={`${P}__lignes`}>
              <span className={`${P}__trait ${P}__trait--courbe`} />
              <span className={`${P}__nombre`}>{pourcent(taux)}</span>
              <span className={`${P}__nom`}>Taux de réussite</span>
              <span className={`${P}__trait ${P}__trait--objectif`} />
              <span className={`${P}__nombre`}>{pourcent(objectif)}</span>
              <span className={`${P}__nom`}>Objectif de mémorisation</span>
              <div className={`${P}__pied`}>
                <span className={`${P}__nombre`}>{nombre(semaine.revisions)}</span>{' '}
                {semaine.revisions > 1
                  ? 'révisions de cartes acquises'
                  : 'révision de carte acquise'}
              </div>
              <div className={`${P}__pied`}>
                <span className={`${P}__nombre`}>{nombre(semaine.aRevoir)}</span>{' '}
                {semaine.aRevoir > 1 ? 'réponses 1 (À revoir)' : 'réponse 1 (À revoir)'}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
